import {
  ClaimMethod,
  ClaimRequestStatus,
  type ClaimRequestViewDto,
} from '@up2stay/up2promo-api-client';

/** Resolved requests stay in the "Last 7 days" section this long (DEV-756). */
export const CLAIM_RECENT_WINDOW_MS = 7 * 24 * 60 * 60 * 1000;
/** Mirrors the backend `CLAIM_POLICY.resendCooldownMs`. */
export const CLAIM_RESEND_COOLDOWN_MS = 60 * 1000;
/** Mirrors the backend `CLAIM_POLICY.maxResends`. */
export const CLAIM_MAX_RESENDS = 5;
/** Mirrors the backend `ClaimRequestManualDto.note` limit. */
export const CLAIM_MANUAL_NOTE_MAX = 500;

/**
 * Claim error codes the API answers with (`errors.<field>`), see the backend
 * `ClaimErrorCode`. `claimRequiresManualVerification` comes from the request
 * route when the product has no setup e-mail.
 */
export type ClaimErrorCode =
  | 'claimInvalid'
  | 'claimExpired'
  | 'claimSuperseded'
  | 'alreadyClaimed'
  | 'claimPending'
  | 'claimResendCooldown'
  | 'claimResendLimit'
  | 'claimMethodNotAllowed'
  | 'claimRequiresManualVerification';

const CLAIM_ERROR_CODES: ReadonlySet<string> = new Set<ClaimErrorCode>([
  'claimInvalid',
  'claimExpired',
  'claimSuperseded',
  'alreadyClaimed',
  'claimPending',
  'claimResendCooldown',
  'claimResendLimit',
  'claimMethodNotAllowed',
  'claimRequiresManualVerification',
]);

export type ClaimApiError = {
  code: ClaimErrorCode | null;
  /** 429 `claimResendCooldown` only. */
  retryAfterSeconds?: number;
};

type ClaimErrorBody = {
  errors?: Record<string, unknown>;
  retryAfterSeconds?: unknown;
};

/** Reads `{ errors: { <field>: code }, retryAfterSeconds? }` from an API error. */
export function readClaimApiError(err: unknown): ClaimApiError {
  if (err === null || typeof err !== 'object') return { code: null };
  const data = (err as { response?: { data?: ClaimErrorBody } }).response?.data;
  const code = Object.values(data?.errors ?? {}).find(
    (value): value is ClaimErrorCode =>
      typeof value === 'string' && CLAIM_ERROR_CODES.has(value),
  );
  const retry = data?.retryAfterSeconds;
  return {
    code: code ?? null,
    ...(typeof retry === 'number' && retry > 0
      ? { retryAfterSeconds: retry }
      : {}),
  };
}

export type ClaimRequestSections = {
  /** PENDING and not yet past `expiresAt`, newest first. */
  pending: ClaimRequestViewDto[];
  /**
   * Left PENDING within the last 7 days without becoming a product
   * (rejected / expired / superseded / cancelled), most recent first.
   */
  recent: ClaimRequestViewDto[];
};

function resolvedAtMs(request: ClaimRequestViewDto): number {
  return Date.parse(request.resolvedAt ?? request.expiresAt);
}

/**
 * Splits the workspace's requests into the drawer sections. A PENDING row
 * whose expiry passed counts as EXPIRED until the API's lazy sweep catches
 * up. VERIFIED rows are left out: the product is in the list by then.
 */
export function partitionClaimRequests(
  requests: readonly ClaimRequestViewDto[],
  now: number,
): ClaimRequestSections {
  const pending: ClaimRequestViewDto[] = [];
  const recent: ClaimRequestViewDto[] = [];
  for (const request of requests) {
    if (request.status === ClaimRequestStatus.PENDING) {
      const expiresAt = Date.parse(request.expiresAt);
      if (expiresAt > now) {
        pending.push(request);
      } else if (now - expiresAt < CLAIM_RECENT_WINDOW_MS) {
        recent.push({ ...request, status: ClaimRequestStatus.EXPIRED });
      }
      continue;
    }
    if (request.status === ClaimRequestStatus.VERIFIED) continue;
    if (now - resolvedAtMs(request) < CLAIM_RECENT_WINDOW_MS) {
      recent.push(request);
    }
  }
  return {
    pending: pending.sort(
      (a, b) => Date.parse(b.createdAt) - Date.parse(a.createdAt),
    ),
    recent: recent.sort((a, b) => resolvedAtMs(b) - resolvedAtMs(a)),
  };
}

/** The workspace's live PENDING request for `hash`, if any. */
export function findPendingClaimForHash(
  requests: readonly ClaimRequestViewDto[] | undefined,
  hash: string,
  now: number,
): ClaimRequestViewDto | undefined {
  const needle = hash.trim();
  return requests?.find(
    (request) =>
      request.hash === needle &&
      request.status === ClaimRequestStatus.PENDING &&
      Date.parse(request.expiresAt) > now,
  );
}

/** When the next resend is allowed: `(lastSentAt ?? createdAt) + 60 s`. */
export function claimResendAvailableAt(request: ClaimRequestViewDto): number {
  return (
    Date.parse(request.lastSentAt ?? request.createdAt) +
    CLAIM_RESEND_COOLDOWN_MS
  );
}

export function isClaimResendLimitReached(
  request: Pick<ClaimRequestViewDto, 'resendCount'>,
): boolean {
  return request.resendCount >= CLAIM_MAX_RESENDS;
}

/** `m:ss` for the resend countdown. */
export function formatCountdown(ms: number): string {
  const totalSeconds = Math.max(0, Math.ceil(ms / 1000));
  const minutes = Math.floor(totalSeconds / 60);
  const seconds = totalSeconds % 60;
  return `${minutes}:${String(seconds).padStart(2, '0')}`;
}

export type ClaimDurationUnit = 'minutes' | 'hours' | 'days';

/**
 * Coarse duration for "expires in" / "sent … ago": minutes below an hour,
 * hours below two days, days above.
 */
export function coarseDuration(ms: number): {
  unit: ClaimDurationUnit;
  count: number;
} {
  const minutes = Math.max(0, Math.round(ms / 60_000));
  if (minutes < 60) return { unit: 'minutes', count: Math.max(1, minutes) };
  const hours = Math.round(minutes / 60);
  if (hours < 48) return { unit: 'hours', count: hours };
  return { unit: 'days', count: Math.round(hours / 24) };
}

/** Status badge key under `claim.status.*` (PENDING splits by method). */
export function claimStatusKey(
  request: Pick<ClaimRequestViewDto, 'status' | 'method'>,
): string {
  if (request.status === ClaimRequestStatus.PENDING) {
    return request.method === ClaimMethod.MANUAL
      ? 'PENDING_MANUAL'
      : 'PENDING_EMAIL';
  }
  return request.status;
}
